import React, { useState, useCallback } from 'react';
import { Task, LogEntry, ProjectMetadata } from '../../types';
import { functions } from '../../firebase';

export interface UseProjectAiGeneratorsParams {
  currentProjectId: string;
  projectName: string;
  projectMetadata: ProjectMetadata;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  updateProjectMetadata: (projectId: string, updates: Partial<ProjectMetadata>) => void;
  addLogEntry: (action: LogEntry['action'], description: string, details?: string) => void;
}

export function useProjectAiGenerators(params: UseProjectAiGeneratorsParams) {
  const {
    currentProjectId,
    projectName,
    projectMetadata,
    setProjectMetadata,
    updateProjectMetadata,
    addLogEntry,
  } = params;

  const [isGeneratingConcept, setIsGeneratingConcept] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const handleGenerateReport = useCallback(
    async (answers?: Record<string, string>) => {
      if (isGeneratingReport) return;
      setIsGeneratingReport(true);
      try {
        const { httpsCallable } = await import('firebase/functions');
        const callGemini = httpsCallable(functions, 'callGemini');

        const answersSummary = answers
          ? Object.entries(answers)
              .map(([q, a]) => `- ${q}: ${a}`)
              .join('\n')
          : 'Keine zusätzlichen Fragen beantwortet.';

        const promptText = `Du bist ein professioneller Projektmanagement-Assistent. Erstelle einen fundierten, strukturierten Projektbericht auf Deutsch für das folgende Projekt:
Projektname: "${projectName}"
Ziele / Vision: ${projectMetadata?.goals || projectMetadata?.vision || 'Keine Angabe'}
Beschreibung / Notizen: ${projectMetadata?.description || 'Keine'}
Antworten auf Statusfragen:
${answersSummary}

Erstelle einen klaren, professionellen Bericht mit Zusammenfassung, aktuellem Status, Meilensteinen und nächsten Schritten.`;

        const payload = {
          model: 'gemini-2.5-flash',
          contents: [
            {
              role: 'user',
              parts: [{ text: promptText }],
            },
          ],
        };

        const result = await callGemini(payload);
        const data = result.data as { text?: string } | null;
        if (data && data.text) {
          setProjectMetadata((prev) => {
            const updated = { ...prev, report: data.text };
            updateProjectMetadata(currentProjectId, updated);
            return updated;
          });
          addLogEntry('ai_plan', `KI-Projektbericht für "${projectName}" generiert`);
        }
      } catch (error) {
        console.error('Report generation failed:', error);
        alert('Fehler bei der Bericht-Generierung. Bitte versuche es erneut.');
      } finally {
        setIsGeneratingReport(false);
      }
    },
    [
      projectName,
      projectMetadata,
      currentProjectId,
      updateProjectMetadata,
      addLogEntry,
      isGeneratingReport,
      setProjectMetadata,
    ]
  );

  const handleGenerateConcept = useCallback(
    async (visibleTasksForConcept: Task[]) => {
      if (isGeneratingConcept) return;
      setIsGeneratingConcept(true);
      try {
        const { httpsCallable } = await import('firebase/functions');
        const callGemini = httpsCallable(functions, 'callGemini');

        const tasksSummary = (visibleTasksForConcept || [])
          .slice(0, 50)
          .map((t) => `- ${t.name} (${t.status || 'todo'}, ${t.start || 'kein Start'} bis ${t.end || 'kein Ende'})`)
          .join('\n');

        const promptText = `Du bist ein erfahrener Projektarchitekt. Erstelle ein klares, professionelles Projektkonzept auf Deutsch für das folgende Projekt:
Projektname: "${projectName}"
Bisherige Beschreibung: ${projectMetadata?.description || 'Noch nicht definiert'}
Aufgabenstruktur:
${tasksSummary || 'Noch keine Aufgaben vorhanden.'}

Strukturiere das Konzept in:
1. Projektziel & Vision
2. Kernmodule / Arbeitspakete
3. Zeitlicher Ablauf & Risiken
4. Handlungsempfehlungen`;

        const payload = {
          model: 'gemini-2.5-flash',
          contents: [
            {
              role: 'user',
              parts: [{ text: promptText }],
            },
          ],
        };

        const result = await callGemini(payload);
        const data = result.data as { text?: string } | null;
        if (data && data.text) {
          setProjectMetadata((prev) => {
            const updated = { ...prev, description: data.text };
            updateProjectMetadata(currentProjectId, updated);
            return updated;
          });
          addLogEntry('ai_plan', `KI-Konzept für "${projectName}" generiert`);
        }
      } catch (error) {
        console.error('Concept generation failed:', error);
        alert('Fehler bei der Konzept-Generierung. Bitte versuche es erneut.');
      } finally {
        setIsGeneratingConcept(false);
      }
    },
    [
      projectName,
      projectMetadata,
      currentProjectId,
      updateProjectMetadata,
      addLogEntry,
      isGeneratingConcept,
      setProjectMetadata,
    ]
  );

  return {
    isGeneratingReport,
    isGeneratingConcept,
    handleGenerateReport,
    handleGenerateConcept,
  };
}
